import React, {useLayoutEffect, useState} from 'react';
import ReactDOM from 'react-dom';

const VIEWPORT_PADDING = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const FloatingMenuPortal = ({
  open,
  anchorRef,
  menuRef,
  children,
  className = "",
  minWidth = 220,
  preferredWidth = null,
  maxHeight = 360,
  minHeight = 120,
  gap = 4
}) => {
  const [layout, setLayout] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setLayout(null);
      return undefined;
    }

    const updateLayout = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom - VIEWPORT_PADDING - gap);
      const spaceAbove = Math.max(0, rect.top - VIEWPORT_PADDING - gap);
      const requiredComfortSpace = Math.min(240, maxHeight);
      const direction = spaceBelow >= requiredComfortSpace || spaceBelow >= spaceAbove ? "down" : "up";
      const availableHeight = direction === "up" ? spaceAbove : spaceBelow;
      const resolvedMaxHeight = Math.max(
        Math.min(minHeight, Math.max(availableHeight, 0)),
        Math.min(maxHeight, Math.max(availableHeight, 0))
      );
      const desiredWidth = Math.max(rect.width, preferredWidth || 0, minWidth);
      const width = Math.min(desiredWidth, Math.max(120, viewportWidth - (VIEWPORT_PADDING * 2)));
      const left = clamp(rect.left, VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING);

      setLayout({
        direction,
        left,
        width,
        maxHeight: Math.max(80, resolvedMaxHeight),
        top: direction === "down" ? rect.bottom + gap : undefined,
        bottom: direction === "up" ? viewportHeight - rect.top + gap : undefined
      });
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    window.addEventListener("scroll", updateLayout, true);

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("scroll", updateLayout, true);
    };
  }, [open, anchorRef, minWidth, preferredWidth, maxHeight, minHeight, gap]);

  if (!open || !layout || typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={className}
      data-open={layout.direction}
      style={{
        position: "fixed",
        zIndex: 20000,
        left: `${layout.left}px`,
        width: `${layout.width}px`,
        top: layout.top !== undefined ? `${layout.top}px` : undefined,
        bottom: layout.bottom !== undefined ? `${layout.bottom}px` : undefined,
        maxHeight: `${layout.maxHeight}px`,
        background: "#fff",
        border: "1px solid #ced4da",
        borderRadius: "0.45rem",
        boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.18)",
        overflow: "hidden"
      }}
    >
      {typeof children === "function" ? children(layout) : children}
    </div>,
    document.body
  );
};

export default FloatingMenuPortal;
